/**
 * Conversion suggestion heuristic for Process records (TGCLOUD-1391).
 *
 * A PURE function: given a Process, it suggests a DEFAULT target to highlight in
 * the opt-in conversion card. It never acts, never converts, and never gates the
 * other options — it only ranks. The customer default map (where present) is the
 * authoritative source; otherwise a light heuristic picks the highlight.
 *
 * This is the JavaScript port of the §6.1 reference implementation. The logic is
 * identical; types are expressed as JSDoc because this codebase is JavaScript,
 * not TypeScript.
 *
 * @typedef {'tag' | 'pathway' | 'note' | 'keep'} ConversionTarget
 *
 * @typedef {Object} ConversionSuggestion
 * @property {ConversionTarget}   suggested  The highlighted default.
 * @property {ConversionTarget[]} options    Ordered alternatives, best-first; always includes 'keep'.
 * @property {string}             rationale  Short, human-readable reason shown in the UI.
 *
 * @typedef {Object} ProcessForSuggestion
 * @property {string}  processType
 * @property {string|null} [processStatus]
 * @property {unknown|null} [processStartDate]  PartialDate | null
 * @property {unknown|null} [endDate]           PartialDate | null
 * @property {string|null}  [processComment]
 * @property {Object<string, ConversionTarget>} [customerDefaultMap]
 *   Per-customer override map: normalised processType -> forced default.
 */

/**
 * @template T
 * @param {T[]} xs
 * @returns {T[]}
 */
function dedupe(xs) {
  return [...new Set(xs)];
}

/**
 * @param {ProcessForSuggestion} p
 * @returns {ConversionSuggestion}
 */
export function suggestConversion(p) {
  const key = p.processType.trim().toLowerCase();

  // 1. Customer-specific mapping (from Phase-0 audit sign-off) always wins as the DEFAULT.
  const forced = p.customerDefaultMap?.[key];
  if (forced) {
    return {
      suggested: forced,
      options: dedupe([forced, 'tag', 'pathway', 'keep']),
      rationale: 'Suggested based on your organisation’s setup.'
    };
  }

  // 2. Real per-record state (dates/comment/owner) => it carries data => Note.
  const hasState =
    !!p.processComment?.trim() || !!p.endDate || !!p.processStartDate;
  // 3. Heuristic signal that members move through steps over time => Pathway.
  const looksLikeWorkflow =
    /wait\s*list|clinic|referral|review|surveillance|triage|screen/.test(key);
  // 4. Otherwise it reads as a membership list => Tag.

  if (looksLikeWorkflow) {
    return {
      suggested: 'pathway',
      options: ['pathway', 'tag', 'keep'],
      rationale:
        'This looks like a workflow patients move through — a Pathway board may suit it.'
    };
  }
  if (hasState) {
    return {
      suggested: 'note',
      options: ['note', 'tag', 'keep'],
      rationale:
        'This record carries dates or comments, so a Note keeps that detail.'
    };
  }
  return {
    suggested: 'tag',
    options: ['tag', 'pathway', 'keep'],
    rationale:
      'A membership list like this can be managed as a Tag — searchable and exportable.'
  };
}
